import type { ConceptTag } from "./ids.ts";
import { isConceptTag } from "./ids.ts";
import type { AuthoredLine, AuthoredMove, CourseSpec } from "./repository.ts";
import { buildCourse } from "./repository.ts";
import { parsePgnTree, type PgnTreeMove } from "./pgn.ts";
import type { ChessConstructor } from "../chess/chessTypes.ts";
import type { LessonCourse, LessonPhase, MoveRole } from "./schema.ts";

const ROLES = new Set<MoveRole>([
  "repertoire",
  "mainline",
  "sideline",
  "transposition",
  "mistake",
  "handoff",
]);

const PHASES = new Set<LessonPhase>(["center", "development", "kingSafety", "plan"]);

export type CommentTags = {
  role?: MoveRole;
  concepts: ConceptTag[];
  variationName?: string;
  eco?: string;
  handoff?: boolean;
  tacticalWarning?: boolean;
  phase?: LessonPhase;
  objective?: string;
  opponentIntent?: string;
  tradeoff?: string;
  explanation?: string;
  piece?: string;
  candidates?: string;
  next?: string;
  watch?: string;
};

// Fields are separated by ";", but notes are prose and use semicolons too, so
// a semicolon only starts a new field when a field name or flag follows it.
const FIELD_BREAK =
  /;\s*(?=(?:role|concepts|var|variation|eco|phase|obj|objective|intent|tradeoff|explanation|why|piece|candidates|next|watch|handoff|warn|warning)\s*(?::|;|$))/i;

export function parseCommentTags(comment: string): CommentTags {
  const tags: CommentTags = { concepts: [] };
  if (!comment.trim()) return tags;
  const chunks = comment
    .split(FIELD_BREAK)
    .map((part) => part.trim())
    .filter(Boolean);
  for (const chunk of chunks) {
    const colon = chunk.indexOf(":");
    if (colon <= 0) {
      const flag = chunk.toLowerCase();
      if (flag === "handoff") tags.handoff = true;
      else if (flag === "warn" || flag === "warning") tags.tacticalWarning = true;
      else if (!tags.explanation) tags.explanation = chunk;
      continue;
    }
    const key = chunk.slice(0, colon).trim().toLowerCase();
    const value = chunk.slice(colon + 1).trim();
    if (key === "role" && ROLES.has(value as MoveRole)) tags.role = value as MoveRole;
    else if (key === "concepts") {
      tags.concepts = value
        .split(",")
        .map((item) => item.trim())
        .filter((item): item is ConceptTag => Boolean(item) && isConceptTag(item));
    } else if (key === "var" || key === "variation") tags.variationName = value;
    else if (key === "eco") tags.eco = value;
    else if (key === "phase" && PHASES.has(value as LessonPhase)) tags.phase = value as LessonPhase;
    else if (key === "obj" || key === "objective") tags.objective = value;
    else if (key === "intent") tags.opponentIntent = value;
    else if (key === "tradeoff") tags.tradeoff = value;
    else if (key === "explanation" || key === "why") tags.explanation = value;
    else if (key === "piece") tags.piece = value;
    else if (key === "candidates") tags.candidates = value;
    else if (key === "next") tags.next = value;
    else if (key === "watch") tags.watch = value;
    else if (key === "handoff") tags.handoff = value !== "false";
    else if (key === "warn" || key === "warning") tags.tacticalWarning = value !== "false";
    else if (!tags.explanation) tags.explanation = chunk;
  }
  return tags;
}

function defaultRole(afterLength: number, inVariation: boolean): MoveRole {
  const white = afterLength % 2 === 0;
  if (inVariation) return "sideline";
  return white ? "repertoire" : "mainline";
}

function flatten(
  moves: PgnTreeMove[],
  after: string[],
  inVariation: boolean,
  lines: AuthoredLine[]
): void {
  for (const move of moves) {
    const tags = parseCommentTags(move.comment);
    const authored: AuthoredMove = {
      san: move.san,
      role: tags.role || defaultRole(after.length, inVariation),
      explanation:
        tags.explanation ||
        (inVariation ? "A known branch from this position." : "Continues the course line."),
      concepts: tags.concepts,
      variationName: tags.variationName,
      objective: tags.objective,
      opponentIntent: tags.opponentIntent,
      handoff: tags.handoff,
      phase: tags.phase,
      tacticalWarning: tags.tacticalWarning,
      tradeoff: tags.tradeoff,
      eco: tags.eco,
      piece: tags.piece,
      candidates: tags.candidates,
      next: tags.next,
      watch: tags.watch,
    };
    lines.push({ after: [...after], move: authored, ...(inVariation ? { branch: true } : {}) });
    for (const variation of move.alternatives) {
      flatten(variation, after, true, lines);
    }
    after = [...after, move.san];
  }
}

export type PgnCourseMeta = {
  id: string;
  openingId: CourseSpec["openingId"];
  slug: string;
  title?: string;
  side?: "white" | "black";
  targetFullMoves: number;
  level: "beginner" | "intermediate";
  objectives: string[];
  contentVersion: number;
  rootObjective: string;
  rootExplanation: string;
  reviewStatus?: CourseSpec["reviewStatus"];
  limitedCoverage?: boolean;
  branchPolicy?: CourseSpec["branchPolicy"];
};

export function pgnToCourseSpec(pgn: string, meta: PgnCourseMeta): CourseSpec {
  const { headers, moves } = parsePgnTree(pgn);
  const lines: AuthoredLine[] = [];
  flatten(moves, [], false, lines);
  return {
    id: meta.id,
    openingId: meta.openingId,
    slug: meta.slug,
    title: meta.title || headers.Opening || meta.openingId,
    side: meta.side || "white",
    targetFullMoves: meta.targetFullMoves,
    level: meta.level,
    objectives: meta.objectives,
    contentVersion: meta.contentVersion,
    rootObjective: meta.rootObjective,
    rootExplanation: meta.rootExplanation,
    lines,
    reviewStatus: meta.reviewStatus,
    limitedCoverage: meta.limitedCoverage,
    branchPolicy: meta.branchPolicy,
  };
}

export function importPgnCourse(
  Chess: ChessConstructor,
  pgn: string,
  meta: PgnCourseMeta
): LessonCourse {
  return buildCourse(Chess, pgnToCourseSpec(pgn, meta));
}
